import type { SortEnd } from "@/editor/components/common/designSystem/SortableList";
import type { UploadingMediaFile } from "@/features/products/hooks/useProductMediaUpload";
import type { Active } from "@dnd-kit/core";
import type { ProductMediaItem } from "schemas/generated/product";

import * as React from "react";

import {
  arrayMove,
  SortableItem,
  SortableList,
} from "@/editor/components/common/designSystem/SortableList";
import { useProductFormEdit } from "@/features/products/hooks/useProductFormEdit";
import { useProductMediaUpload } from "@/features/products/hooks/useProductMediaUpload";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowUp, Image, Plus, Video, X } from "lucide-react";

const ProductMediaDropZone = () => {
  const { watch, dispatchProductEdit } = useProductFormEdit();
  const currentMedia = watch("media") ?? [];
  const [isDragging, setIsDragging] = React.useState(false);

  const { getRootProps, getInputProps, uploadingFiles } = useProductMediaUpload(
    {
      currentMedia,
      onUploadComplete: (newMediaItem: ProductMediaItem) => {
        dispatchProductEdit({
          type: "appendMedia",
          payload: {
            newMediaItem,
          },
        });
      },
    },
  );

  const updateMediaWithSortOrder = (media: ProductMediaItem[]) => {
    const resortedMedia = media.map((item, index) => ({
      ...item,
      sortOrder: index,
    }));
    dispatchProductEdit({
      type: "updateMedia",
      payload: {
        media: resortedMedia,
      },
    });
  };

  const handleRemoveMedia = (assetId: string) => {
    const updatedMedia = currentMedia.filter(
      (item) => item.assetId !== assetId,
    );
    updateMediaWithSortOrder(updatedMedia);
  };

  const handleReorderMedia = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex >= sortedMedia.length) {
      setIsDragging(false);
      return;
    }

    const adjustedNewIndex =
      newIndex >= sortedMedia.length ? sortedMedia.length - 1 : newIndex;

    const reorderedMedia = arrayMove(sortedMedia, oldIndex, adjustedNewIndex);
    updateMediaWithSortOrder(reorderedMedia);
    setIsDragging(false);
  };

  const handleReorderStart = (active: Active) => {
    // Only set dragging state for actual media items (not uploading files or add button)
    // Note: These items have disabled=true so this shouldn't be called for them anyway
    if (
      active.id !== "add-new-media" &&
      !uploadingFiles.some((file) => file.assetId === active.id)
    ) {
      setIsDragging(true);
    }
  };

  const sortedMedia = React.useMemo(
    () => [...currentMedia].sort((a, b) => a.sortOrder - b.sortOrder),
    [currentMedia],
  );

  const hasContent = Boolean(
    currentMedia.length > 0 || uploadingFiles.length > 0,
  );

  if (!hasContent) {
    return (
      <EmptyDropZone
        getRootProps={getRootProps}
        getInputProps={getInputProps}
      />
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <SortableList
        orientation="both-axis"
        onReorderEnd={handleReorderMedia}
        onReorderStart={handleReorderStart}
        withDragHandle={false}
        restrictToParent={true}
        withShadow={true}
      >
        {[
          ...sortedMedia.map((item) => (
            <SortableItem key={item.assetId} id={item.assetId}>
              <MediaItem item={item} onRemove={handleRemoveMedia} />
            </SortableItem>
          )),
          ...uploadingFiles.map((file) => (
            <SortableItem key={file.assetId} id={file.assetId} disabled>
              <UploadingMediaItem file={file} />
            </SortableItem>
          )),
          <SortableItem key="add-new-media" id="add-new-media" disabled>
            <AddMediaButton
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDragging={isDragging}
            />
          </SortableItem>,
        ]}
      </SortableList>
    </div>
  );
};

const MediaItem = ({
  item,
  onRemove,
}: {
  item: ProductMediaItem;
  onRemove: (assetId: string) => void;
}) => {
  const isVideo = item.type === "video";

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onRemove(item.assetId);
  };

  const handleDeletePointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
  };

  return (
    <div className="relative group w-16 h-16">
      <div
        className="w-full h-full rounded-md overflow-hidden bg-cover bg-center after:absolute after:inset-0 after:rounded-md after:border-2 after:border-transparent group-hover:after:border-black after:pointer-events-none cursor-move"
        style={{
          backgroundImage: !isVideo ? `url(${item.url})` : undefined,
        }}
      >
        {isVideo && (
          <div className="w-full h-full flex items-center justify-center bg-sheet">
            <Video size={20} className="text-muted" />
          </div>
        )}
      </div>
      <button
        onClick={handleDeleteClick}
        onPointerDown={handleDeletePointerDown}
        onMouseDown={(e) => e.stopPropagation()}
        className="absolute -top-2 -right-2 w-4 h-4 hover:bg-black hover:border-black hover:text-white transition-all bg-black border-2 border-black rounded-full flex items-center justify-center opacity-0 invisible group-hover:opacity-100 group-hover:visible z-10"
        type="button"
      >
        <X size={8} className="text-white" />
      </button>
    </div>
  );
};

const UploadingMediaItem = ({ file }: { file: UploadingMediaFile }) => {
  const isImage = file.type === "image";

  return (
    <div className="relative w-16 h-16">
      <div className="w-full h-full rounded-md overflow-hidden flex items-center justify-center bg-sheet">
        {isImage ? (
          <div
            className="w-full h-full bg-cover bg-center blur-sm"
            style={{
              backgroundImage: `url(${file.url})`,
            }}
          />
        ) : (
          <Video size={20} className="text-muted" />
        )}
      </div>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-default h-7 w-7 rounded-full flex items-center justify-center">
        <Spinner size={20} variant="white" />
        <ArrowUp
          size={10}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
        />
      </div>
    </div>
  );
};

type DropZoneProps = {
  getRootProps: () => React.HTMLAttributes<HTMLDivElement>;
  getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>;
};

const AddMediaButton = ({
  getRootProps,
  getInputProps,
  isDragging,
}: DropZoneProps & {
  isDragging: boolean;
}) => {
  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <div
        className={twMerge(
          "w-16 h-16 bg-sheet border border-dashed border-border rounded-md flex items-center justify-center transition-colors",
          isDragging
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-sheet/70",
        )}
      >
        <Plus size={16} className="text-muted" />
      </div>
    </div>
  );
};

const EmptyDropZone = ({ getRootProps, getInputProps }: DropZoneProps) => {
  return (
    <div {...getRootProps()} className="flex flex-col gap-1.5 w-full">
      <input {...getInputProps()} />
      <div className="flex justify-center items-center p-4 gap-3 w-full h-16 bg-sheet rounded cursor-pointer hover:bg-sheet/50 transition-colors border border-dashed border-border">
        <div className="relative w-8 h-8">
          <div className="absolute inset-0 flex justify-center items-center w-6 h-6 bg-sheet border border-border rounded-sm transform -rotate-12">
            <Image size={8} className="text-muted" />
          </div>
          <div className="absolute top-1 left-1 flex justify-center items-center w-6 h-6 bg-sheet border border-border rounded-sm">
            <Image size={10} className="text-muted" />
          </div>
        </div>
        <span className="text-sm text-muted text-center">
          Drop your images or video here, or{" "}
          <span className="font-medium">click to browse</span>
        </span>
      </div>
    </div>
  );
};

export default ProductMediaDropZone;
